import { print } from 'graphql';

import { LIMIT_FOR_FETCHING_ADMIN_ITEMS, SORT_TYPE } from '~/shared/constants';
import {
  findByStudentIdLessonTemplateQuery,
  findManyLessonTemplateQuery,
  lessonTemplateFragment,
} from '~/shared/graphql-queries/lesson-templates';
import applyOneLessonTemplateMutation from '~/shared/graphql-queries/lesson-templates/apply-one-lesson-template-mutation';
import createOneLessonTemplateMutation from '~/shared/graphql-queries/lesson-templates/create-one-lesson-template-mutation';
import findByIdLessonTemplateQuery from '~/shared/graphql-queries/lesson-templates/find-by-id-lesson-template-query';
import removeByIdLessonTemplateMutation from '~/shared/graphql-queries/lesson-templates/remove-by-id-lesson-template-mutation';
import updateByIdLessonTemplateMutation from '~/shared/graphql-queries/lesson-templates/update-by-id-form-lesson-mutation';
import updateOneLessonTemplateMutation from '~/shared/graphql-queries/lesson-templates/update-one-lesson-template-mutation';
import { itemsPagination } from '~/shared/helpers/apollo-helpers';
import type { PageInfoType } from '~/shared/types/general-type';
import type { BuilderType } from '~/types';

type PaginationLessonTemplateResponse = Readonly<{
  data?: {
    paginationLessonTemplateQuery?: {
      items: Record<string, unknown>[];
      pageInfo: PageInfoType;
    };
  };
}>;

type PaginationLessonTemplateResult = Readonly<{
  items: Record<string, unknown>[];
  pageInfo: PageInfoType;
}>;

type PaginationLessonTemplateArgs = Readonly<{
  filter?: Record<string, unknown>;
  nextPage?: number;
  sort?: string;
  perPage?: number;
}>;

type RemoveByIdLessonTemplateArgs = Readonly<{
  _id?: string;
}>;

type FindByStudentIdLessonTemplateArgs = Readonly<{
  studentId?: string;
}>;

type FindByIdLessonTemplateArgs = Readonly<{
  _id?: string;
}>;

type FindManyLessonTemplateArgs = Readonly<{
  limit?: number;
  filter?: Record<string, unknown>;
}>;

type UpdateByIdLessonTemplateArgs = Readonly<{
  _id?: string;
  record?: Record<string, unknown>;
}>;

type UpdateOneLessonTemplateArgs = Readonly<{
  course?: string;
  _id?: string;
  isTeacherLessonTemplate?: boolean;
  isSuperTeacherTemplate?: boolean;
  courseCategory?: string;
}>;

type ApplyOneLessonTemplateArgs = Readonly<{
  course?: string;
  isCopyLessonsToTheEndOfBoard?: boolean;
  lessonTemplate?: string;
  student?: string;
  courseCategory?: string;
  mainTemplate?: string;
}>;

type CreateOneLessonTemplateArgs = Readonly<{
  title?: string;
  isNewTemplate?: boolean;
  course?: string;
  courseCategory?: string;
  student?: string;
  isTeacherLessonTemplate?: boolean;
  isPersonalLessonTemplate?: boolean;
}>;

const transformLessonTemplateResponse =
  (type: 'paginationLessonTemplateQuery') =>
  (response: PaginationLessonTemplateResponse): PaginationLessonTemplateResult => {
    return response.data?.[type] ?? { items: [], pageInfo: {} as PageInfoType };
  };

export const lessonTemplatesTagTypes = ['lesson-templates'] as const;

export const getLessonTemplatesQuery = (builder: BuilderType) => {
  return {
    paginationLessonTemplate: builder.query<PaginationLessonTemplateResult, PaginationLessonTemplateArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(
            itemsPagination({
              entity: 'lessonTemplate',
              fragment: lessonTemplateFragment,
            }),
          ),
          variables: {
            filter: args?.filter,
            nextPage: args?.nextPage ?? 1,
            sort: args?.sort ?? SORT_TYPE._ID_DESC,
            perPage: args?.perPage
              ? args.perPage
              : args?.nextPage === 1
                ? LIMIT_FOR_FETCHING_ADMIN_ITEMS + 1
                : LIMIT_FOR_FETCHING_ADMIN_ITEMS,
          },
        }),
      }),
      providesTags: ['lesson-templates'],
      transformResponse: transformLessonTemplateResponse('paginationLessonTemplateQuery'),
    }),

    removeByIdLessonTemplate: builder.mutation<unknown, RemoveByIdLessonTemplateArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(removeByIdLessonTemplateMutation),
          variables: {
            _id: args?._id,
          },
        }),
      }),
    }),

    findByStudentIdLessonTemplate: builder.query<unknown, FindByStudentIdLessonTemplateArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(findByStudentIdLessonTemplateQuery),
          variables: {
            studentId: args?.studentId,
          },
        }),
      }),
    }),

    findByIdLessonTemplate: builder.query<unknown, FindByIdLessonTemplateArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(findByIdLessonTemplateQuery),
          variables: {
            _id: args?._id,
          },
        }),
      }),
    }),

    findManyLessonTemplate: builder.query<unknown, FindManyLessonTemplateArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(findManyLessonTemplateQuery),
          variables: {
            limit: args?.limit,
            filter: args?.filter,
          },
        }),
      }),
    }),

    updateByIdLessonTemplate: builder.mutation<unknown, UpdateByIdLessonTemplateArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(updateByIdLessonTemplateMutation),
          variables: {
            _id: args?._id,
            record: args?.record,
          },
        }),
      }),
    }),

    updateOneLessonTemplate: builder.mutation<unknown, UpdateOneLessonTemplateArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(updateOneLessonTemplateMutation),
          variables: {
            course: args?.course,
            _id: args?._id,
            isTeacherLessonTemplate: args?.isTeacherLessonTemplate,
            isSuperTeacherTemplate: args?.isSuperTeacherTemplate,
            courseCategory: args?.courseCategory,
          },
        }),
      }),
    }),

    applyOneLessonTemplate: builder.mutation<unknown, ApplyOneLessonTemplateArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(applyOneLessonTemplateMutation),
          variables: {
            course: args?.course,
            isCopyLessonsToTheEndOfBoard: args?.isCopyLessonsToTheEndOfBoard,
            lessonTemplate: args?.lessonTemplate,
            student: args?.student,
            courseCategory: args?.courseCategory,
            mainTemplate: args?.mainTemplate,
          },
        }),
      }),
    }),

    createOneLessonTemplate: builder.mutation<unknown, CreateOneLessonTemplateArgs>({
      query: (args) => ({
        url: '',
        method: 'POST',
        body: JSON.stringify({
          query: print(createOneLessonTemplateMutation),
          variables: {
            title: args?.title,
            isNewTemplate: args?.isNewTemplate,
            course: args?.course,
            courseCategory: args?.courseCategory,
            student: args?.student,
            isTeacherLessonTemplate: args?.isTeacherLessonTemplate,
            isPersonalLessonTemplate: args?.isPersonalLessonTemplate,
          },
        }),
      }),
    }),
  };
};
